/**
 * Statuses, stat stages and charges held: applying and curing statuses (the
 * `status` and `cure` effects), stage changes (`stat_stage`), interrupting a
 * charge, the burn and poison ticks and the countdown to expiry (both at the
 * end of the holder's own turn, round.ts).
 */
import type { StatusRules } from '@ab/content-schema';
import { dealDamage, maxHp } from './combat.ts';
import { type ApplyResult, type Ctx, chain, none } from './commands/types.ts';
import { idiv } from './determinism.ts';
import type { Event } from './events.ts';
import { type RunState, type StageStat, type Status, type Unit, findUnit, hasStatus, updateUnit } from './state.ts';
import { stageTotal } from './stats.ts';

export { hasStatus };

/** Cancels the charge a unit holds (`charge_interrupted`). Nothing if it holds none. */
export const interruptCharge = (state: RunState, unitId: string): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.charging === null) return none(state);
  return {
    state: updateUnit(state, unitId, (x) => ({ ...x, charging: null })),
    events: [{ type: 'charge_interrupted', unit: unitId, skill: u.charging.skill }],
  };
};

/**
 * Gives a standing unit `status` for `turns` of its own turns
 * (`status_applied`). A status it already has is reset to the new duration
 * (keeping its place in the list). `stun` and `disrupt` interrupt a charge
 * the unit holds.
 */
export const applyStatus = (state: RunState, unitId: string, status: Status, turns: number): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down) return none(state);
  const statuses = hasStatus(u, status) ? u.statuses.map((s) => (s.status === status ? { ...s, turns } : s)) : [...u.statuses, { status, turns }];
  const applied: ApplyResult = {
    state: updateUnit(state, unitId, (x) => ({ ...x, statuses })),
    events: [{ type: 'status_applied', unit: unitId, status }],
  };
  if (status !== 'stun' && status !== 'disrupt') return applied;
  const interrupted = interruptCharge(applied.state, unitId);
  return { state: interrupted.state, events: [...applied.events, ...interrupted.events] };
};

/** Removes the listed statuses (or all), emitting `status_cured` for each one the unit had, in the unit's order. */
export const cureStatus = (state: RunState, unitId: string, which: readonly Status[] | 'all'): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down) return none(state);
  const cured = u.statuses.filter((s) => which === 'all' || which.includes(s.status));
  if (cured.length === 0) return none(state);
  return {
    state: updateUnit(state, unitId, (x) => ({ ...x, statuses: x.statuses.filter((s) => !cured.includes(s)) })),
    events: cured.map((s): Event => ({ type: 'status_cured', unit: unitId, status: s.status })),
  };
};

/**
 * A hit lands on a unit (a basic attack or skill that passed its accuracy
 * roll, or an item, before any effect): a standing sleeper wakes at once,
 * whether or not the hit then deals damage (`woke_up`). Nothing otherwise.
 */
export const wakeUp = (state: RunState, unitId: string): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down || !hasStatus(u, 'sleep')) return none(state);
  return {
    state: updateUnit(state, unitId, (x) => ({ ...x, statuses: x.statuses.filter((s) => s.status !== 'sleep') })),
    events: [{ type: 'woke_up', unit: unitId }],
  };
};

/** The total `stat_stage` would leave `stat` at: `clamp(total + stages, −6, 6)`. */
export const stageAfter = (u: Unit, stat: StageStat, stages: number): number => Math.max(-6, Math.min(6, stageTotal(u.stages, stat) + stages));

/**
 * Moves a stat by `stages`, keeping its total within −6..+6: the stored change
 * is clamped so the total lands on the limit. `turns` null lasts the battle.
 * Emits `stage_changed` with the new total only when the total changes; at the
 * limit nothing is stored and nothing is emitted.
 */
export const addStage = (state: RunState, unitId: string, stat: StageStat, stages: number, turns: number | null): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down) return none(state);
  const before = stageTotal(u.stages, stat);
  const delta = stageAfter(u, stat, stages) - before;
  if (delta === 0) return none(state);
  const next = [...u.stages, { stat, stages: delta, turns }];
  return {
    state: updateUnit(state, unitId, (x) => ({ ...x, stages: next })),
    events: [{ type: 'stage_changed', unit: unitId, stat, stages: stageTotal(next, stat) }],
  };
};

const tick = (ctx: Ctx, state: RunState, unitId: string, status: 'burn' | 'poison', rate: StatusRules['burnDamage']): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down || !hasStatus(u, status)) return none(state);
  const amount = Math.max(1, idiv(maxHp(ctx, u) * rate.num, rate.den));
  return dealDamage(ctx, state, unitId, amount, null, null, null, status);
};

/**
 * Burn, then poison, at the end of the holder's own turn (round.ts): a
 * fraction of max HP each, minimum 1. Status damage has no source and no
 * element, so it never wakes a sleeper. Poison does not tick on a holder the
 * burn tick defeated.
 */
export const tickStatusDamage = (ctx: Ctx, state: RunState, unitId: string): ApplyResult => {
  const rules = ctx.content.rules.statuses;
  return chain(
    state,
    (s) => tick(ctx, s, unitId, 'burn', rules.burnDamage),
    (s) => tick(ctx, s, unitId, 'poison', rules.poisonDamage),
  );
};

type StageEntry = Unit['stages'][number];

/**
 * Rewrites one stat's permanent stage entries as a single entry so that the
 * stored entries sum to the clamped total: `clamp(sum, −6, 6) − sum(timed)`,
 * omitted when 0. Needed after timed entries expire, because the entries left
 * can sum outside −6..+6 (permanent +6, timed −6, permanent +6 leaves +12).
 */
const normaliseStages = (stages: readonly StageEntry[], stat: StageStat): StageEntry[] => {
  let sum = 0;
  let timed = 0;
  for (const s of stages) {
    if (s.stat !== stat) continue;
    sum += s.stages;
    if (s.turns !== null) timed += s.stages;
  }
  const permanent = Math.max(-6, Math.min(6, sum)) - timed;
  const rest = stages.filter((s) => s.stat !== stat || s.turns !== null);
  return permanent === 0 ? rest : [...rest, { stat, stages: permanent, turns: null }];
};

/**
 * Runs at the end of the unit's own turn (round.ts), whether it acted or was
 * skipped. Status turns and timed stage turns count down by one; those
 * reaching 0 expire (`status_expired`; for stages, the stat is normalised by
 * `normaliseStages` and one `stage_changed` with its new total is emitted per
 * stat whose total changed, in the order the stats first expired).
 */
export const countDown = (state: RunState, unitId: string): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down) return none(state);
  const events: Event[] = [];
  const statuses = u.statuses.map((s) => ({ ...s, turns: s.turns - 1 }));
  for (const s of statuses) if (s.turns <= 0) events.push({ type: 'status_expired', unit: unitId, status: s.status });
  const stages = u.stages.map((s) => (s.turns === null ? s : { ...s, turns: s.turns - 1 }));
  const expired: StageStat[] = [];
  for (const s of stages) if (s.turns !== null && s.turns <= 0 && !expired.includes(s.stat)) expired.push(s.stat);
  let kept: StageEntry[] = stages.filter((s) => s.turns === null || s.turns > 0);
  for (const stat of expired) {
    kept = normaliseStages(kept, stat);
    const total = stageTotal(kept, stat);
    if (total !== stageTotal(u.stages, stat)) events.push({ type: 'stage_changed', unit: unitId, stat, stages: total });
  }
  const next = updateUnit(state, unitId, (x) => ({ ...x, statuses: statuses.filter((s) => s.turns > 0), stages: kept }));
  return { state: next, events };
};
